import { useEffect, type ReactNode } from "react";

// Relative: vite.config.ts aliases only a fixed list of top-level folders and
// `features` is not one, while tsconfig's catch-all path mapping resolves it.
// A bare "features/..." specifier therefore typechecks and fails in the browser.
import { requestProvisioning } from "../profile/use-user-profile";

import { SessionProvider, useSessionFacts } from "./session";
import { useAuthUser } from "./useAuthUser";

/**
 * Publishes the session for the whole app, and makes sure the signed-in user
 * has a profile. Mounted once, at the router root.
 *
 * Not inside RequireAuth, which is where this started: every gated route mounts
 * its OWN RequireAuth, so a provider there tears down and rebuilds on each
 * navigation — the role-gated nav items would blink out and back on every route
 * change, at the cost of two extra Firestore reads each time. It also has to
 * outrank the gate because /e/$slug deliberately does not use RequireAuth, and
 * the rail inside an event still has to know who is looking at it.
 *
 * Separate from session.tsx so that module stays free of firebase/auth: AppShell
 * imports useSession, and integrations/firebase/auth calls getAuth() at module
 * load, which throws without a real API key. Keeping the auth dependency here
 * means only the router root pays for it.
 */
export function SessionBoundary({ children }: { children: ReactNode }) {
  const { user } = useAuthUser();
  const session = useSessionFacts(user?.uid ?? null, user?.email ?? null);

  // Firebase gen2 Python has no post-create Auth trigger, so the client files
  // the one-shot command that creates users/{uid}.
  //
  // Here rather than in RequireAuth, because the gate is not where sign-in
  // happens any more. /e/$slug signs an invitee in without ever mounting
  // RequireAuth, and a user with no profile is not a cosmetic problem: they do
  // not appear in the governance user list, so they cannot be made an event
  // admin, and SET_EVENT_ADMIN refuses a uid with no profile. Every route sits
  // under this boundary, so no route can forget.
  const uid = user?.uid;
  useEffect(() => {
    if (uid) void requestProvisioning(uid);
  }, [uid]);

  return <SessionProvider value={session}>{children}</SessionProvider>;
}
